<template>
  <div :class="['Hero', 'PageFullWidth', { 'Hero--fullHeight': fullHeight }]">
    <ImageBackground
      :sources="imageSources"
      class="Hero-background"
      :class="{ 'Hero-overlay': title || description }"
      spaceTop="0px"
      spaceBottom="0px"
      spaceX="0px"
    >
      <div class="Hero-content PageContainer">
        <div class="Hero-text">
          <slot name="title">
            <h1 class="Hero-title" v-html="title" />
          </slot>

          <slot name="description">
            <p v-if="description" class="Hero-description" v-html="description" />
          </slot>
        </div>

        <div v-if="$slots.action || buttonLabel" class="Hero-action">
          <slot name="action">
            <ActionButton
              v-if="buttonLabel"
              size="lg"
              :leftIcon="buttonLeftIcon"
              :rightIcon="buttonRightIcon"
              @click="$emit('action-click')"
            >
              {{ buttonLabel }}
            </ActionButton>
          </slot>
        </div>
      </div>
    </ImageBackground>
  </div>
</template>

<script setup lang="ts">
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import ImageBackground from '../../atoms/ImageBackground/ImageBackground.vue';
import type { IconName } from '../../atoms/Icon/Icon.model';

export interface HeroProps {
  /** Hero title text */
  title?: string;
  /** Hero description text */
  description?: string;
  /** Image sources with optional media queries and types */
  imageSources: Array<{
    src: string;
    media?: string;
    type?: string;
  }>;
  /** Action button label */
  buttonLabel?: string;
  /** Left icon for button */
  buttonLeftIcon?: IconName;
  /** Right icon for button */
  buttonRightIcon?: IconName;
  /** Custom height for the hero component */
  height?: string;
  /** Top spacing above the hero component */
  spaceTop?: string;
  /** Bottom spacing below the hero component */
  spaceBottom?: string;
  /** Full height for the hero component */
  fullHeight?: boolean;
}

withDefaults(defineProps<HeroProps>(), {
  spaceTop: '250px',
  spaceBottom: '100px',
});

defineEmits<{
  'action-click': [];
}>();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Hero {
   --container-top-padding: v-bind("spaceTop");
   --container-bottom-padding: v-bind("spaceBottom");

    overflow: hidden;

    @include responsive.tablet-only {
      --container-top-padding: #{vars.$dimensions-d-20};
      --container-bottom-padding: #{vars.$dimensions-d-20};
    }

    @include responsive.mobile {
      min-block-size: 420px;
      align-items: center;
    }

    &--fullHeight {
      height: calc(100vh - vars.$header-min-height-desktop);

      &-background {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }

    &-background {
      background-position: center;

      padding-top: var(--container-top-padding);
      padding-bottom: var(--container-bottom-padding);

      @include responsive.tablet-only {
        padding-top: vars.$dimensions-d-20;
        padding-bottom: vars.$dimensions-d-20;
      }
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-5;

      @include responsive.mobile {
        align-self: stretch;
      }
    }

    &-text {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-4;

      @include responsive.tablet-only {
        inline-size: 75%;
      }

      @include responsive.desktop {
        inline-size: 60%;
      }
    }

    &-title {
      margin: 0;
      color: vars.$molecules-hero-title-color;
      font-family: vars.$font-family-heading;
      font-weight: vars.$typography-weight-heading;
    }

    &-description {
      margin: 0;
      color: vars.$molecules-hero-title-color;
      @include text.text('lg');

      @include responsive.mobile {
        @include text.text('md');
      }
    }

    &-overlay {
      .ImageBackground-picture::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(0deg, rgba(51, 51, 51, 0.20) 0%, rgba(51, 51, 51, 0.00) 59.62%);
      }
    }

    .ImageBackground-picture {
      border-radius: 0;
    }
  }
}
</style>
